import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { ApiOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { get, post, put } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import './PolarisReportingPage.css';

interface PolarisConfig {
  app_id: string;
  app_secret_masked: string;
  credential_configured: boolean;
  enabled: boolean;
  app_type: string;
  product_code: string;
  product_name: string;
  source_version: string;
  sync_method: string;
  sync_path: string;
  sync_url: string;
  public_base_url: string;
  pull_interval_minutes: number;
  reporting_mode: string;
  payment_included: boolean;
  platform_hint: string;
  updated_at?: string;
}

interface PolarisStats {
  total: number;
  success: number;
  failed: number;
  last_requested_at?: string;
  last_success_at?: string;
}

interface PolarisLog {
  id: string;
  requested_at?: string;
  app_id: string;
  start_date: string;
  end_date: string;
  client_ip: string;
  status: 'success' | 'failed';
  http_status: number;
  result_code: string;
  message: string;
  duration_ms: number;
  response_summary: {
    item_count?: number;
    registered_user_total?: number;
    active_user_total?: number;
    flow_task_total?: number;
  };
}

interface LogResponse {
  total: number;
  page: number;
  page_size: number;
  data: PolarisLog[];
}

interface ConnectivityResult {
  success: boolean;
  target: string;
  http_status: number;
  result_code: string;
  message: string;
  duration_ms: number;
  item_count: number;
}

const formatTime = (value?: string) => (value ? new Date(value).toLocaleString() : '-');

const PolarisReportingPage: React.FC = () => {
  const [form] = Form.useForm();
  const [config, setConfig] = useState<PolarisConfig | null>(null);
  const [stats, setStats] = useState<PolarisStats>({ total: 0, success: 0, failed: 0 });
  const [logs, setLogs] = useState<PolarisLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<ConnectivityResult | null>(null);

  const loadConfigAndStats = useCallback(async () => {
    const [configData, statsData] = await Promise.all([
      get<PolarisConfig>(API_ENDPOINTS.polaris_app.config),
      get<PolarisStats>(API_ENDPOINTS.polaris_app.stats),
    ]);
    setConfig(configData);
    setStats(statsData);
    form.setFieldsValue({
      enabled: configData.enabled,
      product_code: configData.product_code,
      product_name: configData.product_name,
      source_version: configData.source_version,
      public_base_url: configData.public_base_url,
    });
  }, [form]);

  const loadLogs = useCallback(async () => {
    const query = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (statusFilter) query.set('status', statusFilter);
    const data = await get<LogResponse>(`${API_ENDPOINTS.polaris_app.logs}?${query.toString()}`);
    setLogs(data.data);
    setTotal(data.total);
  }, [page, pageSize, statusFilter]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadConfigAndStats(), loadLogs()]);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载北极星配置失败');
    } finally {
      setLoading(false);
    }
  }, [loadConfigAndStats, loadLogs]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const updated = await put<PolarisConfig>(API_ENDPOINTS.polaris_app.config, values);
      setConfig(updated);
      message.success('北极星数据上报配置已保存');
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleConnectivityTest = async () => {
    setTesting(true);
    try {
      const result = await post<ConnectivityResult>(API_ENDPOINTS.polaris_app.connectivityTest);
      setTestResult(result);
      if (result.success) message.success('北极星拉取链路正常');
      else message.error(result.message || '北极星拉取链路失败');
      await Promise.all([loadLogs(), loadConfigAndStats()]);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '连通性测试失败');
    } finally {
      setTesting(false);
    }
  };

  const columns: ColumnsType<PolarisLog> = useMemo(
    () => [
      { title: '拉取时间', dataIndex: 'requested_at', width: 180, render: formatTime },
      {
        title: '状态',
        dataIndex: 'status',
        width: 90,
        render: (value: string) =>
          value === 'success' ? <Tag color="success">成功</Tag> : <Tag color="error">失败</Tag>,
      },
      { title: '日期范围', width: 210, render: (_, row) => `${row.start_date || '-'} ～ ${row.end_date || '-'}` },
      { title: '来源 IP', dataIndex: 'client_ip', width: 140 },
      {
        title: '返回摘要',
        width: 250,
        render: (_, row) =>
          row.status === 'success' ? (
            <Space size={4} wrap>
              <Tag>{row.response_summary.item_count || 0} 天</Tag>
              <Tag>{row.response_summary.active_user_total || 0} 活跃</Tag>
              <Tag>{row.response_summary.flow_task_total || 0} 任务</Tag>
            </Space>
          ) : (
            <Typography.Text type="danger">{row.result_code}: {row.message}</Typography.Text>
          ),
      },
      { title: 'HTTP', dataIndex: 'http_status', width: 80 },
      { title: '耗时', dataIndex: 'duration_ms', width: 90, render: (value: number) => `${value} ms` },
    ],
    [],
  );

  return (
    <div className="polaris-page">
      <div className="polaris-header">
        <div>
          <h1 className="page-title">北极星数据上报</h1>
          <p>管理声屿运营数据的北极星拉取配置，并查看每一次真实拉取记录。</p>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={loadAll}>刷新</Button>
          <Button type="primary" icon={<ApiOutlined />} loading={testing} onClick={handleConnectivityTest}>
            模拟拉取今日数据
          </Button>
        </Space>
      </div>

      <Alert
        showIcon
        type="info"
        message="上报方式：北极星中台主动拉取"
        description="北极星在应用配置中保存下方完整接口地址，开启自动同步后每 5 分钟携带 App ID 和 App Secret 拉取一次。本项目不向北极星主动推送，也不接入支付。"
      />

      <Row gutter={[16, 16]} className="polaris-stats">
        <Col xs={12} md={6}><Card><Statistic title="累计拉取" value={stats.total} suffix="次" /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="成功" value={stats.success} valueStyle={{ color: '#389e0d' }} suffix="次" /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="失败" value={stats.failed} valueStyle={{ color: stats.failed ? '#cf1322' : undefined }} suffix="次" /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="最近成功" value={formatTime(stats.last_success_at)} /></Card></Col>
      </Row>

      <Card title="接入信息" className="polaris-card" loading={loading && !config}>
        {config && (
          <Descriptions bordered column={{ xs: 1, md: 2 }}>
            <Descriptions.Item label="App ID（Key）"><Typography.Text copyable>{config.app_id}</Typography.Text></Descriptions.Item>
            <Descriptions.Item label="App Secret">{config.app_secret_masked} {config.credential_configured ? <Tag color="success">已配置</Tag> : <Tag color="error">未配置</Tag>}</Descriptions.Item>
            <Descriptions.Item label="应用类型"><Tag color="blue">app</Tag></Descriptions.Item>
            <Descriptions.Item label="同步频率">北极星每 {config.pull_interval_minutes} 分钟拉取</Descriptions.Item>
            <Descriptions.Item label="完整拉取地址" span={2}><Typography.Text code copyable>{config.sync_url}</Typography.Text></Descriptions.Item>
            <Descriptions.Item label="请求方式"><Tag>{config.sync_method}</Tag></Descriptions.Item>
            <Descriptions.Item label="支付数据"><Tag>不接入，支付指标返回 0</Tag></Descriptions.Item>
            <Descriptions.Item label="日活平台口径" span={2}>{config.platform_hint}</Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      {testResult && (
        <Alert
          showIcon
          closable
          onClose={() => setTestResult(null)}
          type={testResult.success ? 'success' : 'error'}
          message={testResult.success ? '拉取链路测试成功' : '拉取链路测试失败'}
          description={`${testResult.result_code} · HTTP ${testResult.http_status || '-'} · ${testResult.duration_ms} ms · 返回 ${testResult.item_count} 天数据。${testResult.message}`}
          className="polaris-test-result"
        />
      )}

      <Card title="上报配置" className="polaris-card">
        <Form form={form} layout="vertical" className="polaris-form">
          <Row gutter={16}>
            <Col xs={24} md={6}><Form.Item label="启用数据同步" name="enabled" valuePropName="checked"><Switch checkedChildren="启用" unCheckedChildren="停用" /></Form.Item></Col>
            <Col xs={24} md={6}><Form.Item label="产品编码" name="product_code" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col xs={24} md={6}><Form.Item label="产品名称" name="product_name" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col xs={24} md={6}><Form.Item label="统计口径版本" name="source_version" rules={[{ required: true }]}><Input /></Form.Item></Col>
          </Row>
          <Form.Item
            label="产品公网访问地址"
            name="public_base_url"
            tooltip="北极星中台可访问的协议、域名/IP 和端口；系统会自动拼接 /api/v1/polaris/sync"
            rules={[{ type: 'url', warningOnly: true, message: '请输入完整的 HTTP/HTTPS 地址' }]}
          >
            <Input placeholder="例如 http://服务器IP:54180" />
          </Form.Item>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>保存配置</Button>
        </Form>
      </Card>

      <Card
        title="拉取记录"
        className="polaris-card"
        extra={
          <Select
            value={statusFilter}
            style={{ width: 120 }}
            options={[{ value: '', label: '全部状态' }, { value: 'success', label: '成功' }, { value: 'failed', label: '失败' }]}
            onChange={(value) => { setStatusFilter(value); setPage(1); }}
          />
        }
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={logs}
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (value) => `共 ${value} 条`,
            onChange: (nextPage, nextSize) => { setPage(nextPage); setPageSize(nextSize); },
          }}
        />
      </Card>
    </div>
  );
};

export default PolarisReportingPage;
